/**
 * Registration Page Component
 *
 * New user registration interface with:
 * - Email, username, and password fields
 * - Name and company information
 * - Password strength indicator
 * - Terms and conditions acceptance
 * - Email verification workflow
 */

import React, { useState, useMemo } from 'react';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  Building2,
  AlertCircle,
  Loader2,
  CheckCircle2,
  UserPlus,
  ArrowLeft,
  Shield,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUserByEmail, getUserByUsername } from '@business-assistant/shared';
import { useTranslation } from '../hooks';

// =============================================================================
// TYPES
// =============================================================================

interface RegisterPageProps {
  onRegisterSuccess?: (userId: string) => void;
  onLoginClick?: () => void;
}

type RegistrationStep = 'account' | 'profile' | 'verification';

type PasswordStrength = 'weak' | 'medium' | 'strong';

// =============================================================================
// HELPERS
// =============================================================================

function getPasswordStrength(password: string): PasswordStrength {
  if (password.length < 6) return 'weak';

  let score = 0;
  if (password.length >= 8) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 1) return 'weak';
  if (score <= 2) return 'medium';
  return 'strong';
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function RegisterPage({ onRegisterSuccess, onLoginClick }: RegisterPageProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const [step, setStep] = useState<RegistrationStep>('account');
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  // Account fields
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Profile fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');

  // Verification
  const [verificationCode, setVerificationCode] = useState('');

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Password strength
  const passwordStrength = useMemo(
    () => getPasswordStrength(password),
    [password]
  );

  // Validate account step
  const validateAccountStep = () => {
    setError(null);

    // Email validation
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('register.errors.invalidEmail'));
      return false;
    }

    // Check if email already exists
    if (getUserByEmail(email)) {
      setError(t('register.errors.emailExists'));
      return false;
    }

    // Username validation
    if (!username || username.length < 3) {
      setError(t('register.errors.usernameMinLength'));
      return false;
    }

    // Check if username already exists
    if (getUserByUsername(username)) {
      setError(t('register.errors.usernameExists'));
      return false;
    }

    // Password validation
    if (!password || password.length < 6) {
      setError(t('register.errors.passwordMinLength'));
      return false;
    }

    if (password !== confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return false;
    }

    if (!acceptedTerms) {
      setError(t('register.errors.acceptTerms'));
      return false;
    }

    return true;
  };

  // Handle account step submission
  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAccountStep()) {
      return;
    }

    setIsLoading(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsLoading(false);
    setStep('profile');
  };

  // Handle profile step submission
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const result = await auth.register({
      email,
      username,
      password,
      firstName,
      lastName,
      phone,
      companyName,
    });

    setIsLoading(false);

    if (!result.success) {
      setError(result.error || t('register.errors.genericError'));
      return;
    }

    if (result.requiresEmailVerification) {
      setStep('verification');
    } else {
      // Registration complete without email verification (unlikely)
      onRegisterSuccess?.('success');
    }
  };

  // Handle verification submission
  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    // For demo, accept the default code "123456"
    // In production, this would call verifyEmail API
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (verificationCode !== '123456') {
      setError(t('register.errors.invalidVerificationCode'));
      setIsLoading(false);
      return;
    }

    // Registration successful
    setIsLoading(false);
    onRegisterSuccess?.('success');
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Header */}
        <div className="auth-header">
          <div className="auth-logo">
            <Building2 size={40} />
          </div>
          <h1>{t('register.createAccount')}</h1>
          <p className="auth-subtitle">
            {step === 'account' && t('register.subtitles.account')}
            {step === 'profile' && t('register.subtitles.profile')}
            {step === 'verification' && t('register.subtitles.verification')}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="auth-progress">
          <div className={`auth-progress-step ${step === 'account' || step === 'profile' || step === 'verification' ? 'active' : ''}`}>
            <div className="auth-progress-circle">1</div>
            <span>{t('register.steps.account')}</span>
          </div>
          <div className="auth-progress-line"></div>
          <div className={`auth-progress-step ${step === 'profile' || step === 'verification' ? 'active' : ''}`}>
            <div className="auth-progress-circle">2</div>
            <span>{t('register.steps.profile')}</span>
          </div>
          <div className="auth-progress-line"></div>
          <div className={`auth-progress-step ${step === 'verification' ? 'active' : ''}`}>
            <div className="auth-progress-circle">3</div>
            <span>{t('register.steps.verification')}</span>
          </div>
        </div>

        {/* Account Step */}
        {step === 'account' && (
          <form className="auth-form" onSubmit={handleAccountSubmit}>
            {/* Email */}
            <div className="auth-field">
              <label className="auth-label">
                <Mail size={16} />
                Email
              </label>
              <input
                type="email"
                className="auth-input"
                placeholder="mario.rossi@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            {/* Username */}
            <div className="auth-field">
              <label className="auth-label">
                <User size={16} />
                Username
              </label>
              <input
                type="text"
                className="auth-input"
                placeholder="mrossi"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            {/* Password */}
            <div className="auth-field">
              <label className="auth-label">
                <Lock size={16} />
                Password
              </label>
              <div className="auth-input-with-icon">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-input-icon-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {password && (
                <div className="password-strength">
                  <div className={`password-strength-bar ${passwordStrength}`}>
                    <div className="password-strength-fill"></div>
                  </div>
                  <span className={`password-strength-label ${passwordStrength}`}>
                    {passwordStrength === 'weak' && t('register.passwordStrength.weak')}
                    {passwordStrength === 'medium' && t('register.passwordStrength.medium')}
                    {passwordStrength === 'strong' && t('register.passwordStrength.strong')}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="auth-field">
              <label className="auth-label">
                <Lock size={16} />
                {t('register.confirmPassword')}
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                className="auth-input"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {/* Terms */}
            <div className="auth-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
                <span>
                  {t('register.acceptTermsPrefix')}{' '}
                  <a href="#" className="auth-link">
                    {t('register.termsAndConditions')}
                  </a>
                </span>
              </label>
            </div>

            {/* Error */}
            {error && (
              <div className="auth-error">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Submit */}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 size={18} className="spinner" />
                  {t('register.creatingAccount')}
                </>
              ) : (
                <>
                  {t('register.continue')}
                </>
              )}
            </button>
          </form>
        )}

        {/* Profile Step */}
        {step === 'profile' && (
          <form className="auth-form" onSubmit={handleProfileSubmit}>
            <div className="auth-field-group">
              <div className="auth-field">
                <label className="auth-label">{t('register.firstName')}</label>
                <input
                  type="text"
                  className="auth-input"
                  placeholder="Mario"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="auth-field">
                <label className="auth-label">{t('register.lastName')}</label>
                <input
                  type="text"
                  className="auth-input"
                  placeholder="Rossi"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">
                <Building2 size={16} />
                {t('register.companyOptional')}
              </label>
              <input
                type="text"
                className="auth-input"
                placeholder="Acme Srl"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>

            <div className="auth-field">
              <label className="auth-label">{t('register.phoneOptional')}</label>
              <input
                type="tel"
                className="auth-input"
                placeholder="+39 348 1234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="auth-error">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div className="auth-form-actions">
              <button
                type="button"
                className="auth-button-secondary"
                onClick={() => setStep('account')}
              >
                <ArrowLeft size={18} />
                {t('register.back')}
              </button>
              <button type="submit" className="auth-button" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="spinner" />
                    {t('register.sending')}
                  </>
                ) : (
                  <>
                    {t('register.continue')}
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Verification Step */}
        {step === 'verification' && (
          <form className="auth-form" onSubmit={handleVerificationSubmit}>
            <div className="auth-verification-header">
              <Mail size={48} />
              <p>
                {t('register.verificationSent', { email })}
              </p>
            </div>

            <div className="auth-field">
              <label className="auth-label">{t('register.verificationCode')}</label>
              <input
                type="text"
                className="auth-input auth-input-code"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                required
                autoFocus
                maxLength={6}
              />
            </div>

            <p className="auth-hint">
              {t('register.didntReceiveCode')}{' '}
              <button type="button" className="auth-link">
                {t('register.resendCode')}
              </button>
            </p>

            {/* Error */}
            {error && (
              <div className="auth-error">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Submit */}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 size={18} className="spinner" />
                  {t('register.verifying')}
                </>
              ) : (
                <>
                  <UserPlus size={18} />
                  {t('register.completeRegistration')}
                </>
              )}
            </button>

            <button
              type="button"
              className="auth-link-button"
              onClick={() => setStep('profile')}
            >
              <ArrowLeft size={16} />
              {t('register.goBack')}
            </button>
          </form>
        )}

        {/* Login Link */}
        {step === 'account' && (
          <div className="auth-footer">
            <span>{t('register.haveAccount')}</span>
            <button className="auth-link" onClick={onLoginClick}>
              {t('register.login')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
